import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';

interface DeleteReviewParams {
  reviewId: number;
  moderationNotes: string;
}

/**
 * Composable for deleting a review permanently
 * Performs optimistic updates to remove the review from all lists
 */
export function useDeleteReview() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['admin', 'deleteReview'],
    mutationFn: async ({ reviewId, moderationNotes }: DeleteReviewParams) => {
      return await api.admin.reviewsV2.deleteReview({
        params: { review: reviewId },
        body: { moderation_notes: moderationNotes },
      });
    },
    onMutate: async ({ reviewId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['admin', 'reviews'] });

      // Snapshot all query states
      const previousData: Record<string, any> = {};
      ['all', 'published', 'pending'].forEach(status => {
        previousData[status] = queryClient.getQueryData(['admin', 'reviews', status]);
      });

      // Optimistically remove from all caches
      ['all', 'published', 'pending'].forEach(status => {
        queryClient.setQueryData(['admin', 'reviews', status], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: page.data.filter((review: any) => review.id !== reviewId),
            })),
          };
        });
      });

      return { previousData };
    },
    onError: (error: any, _variables, context) => {
      // Revert the optimistic updates on error
      if (context?.previousData) {
        Object.entries(context.previousData).forEach(([status, data]) => {
          if (data) {
            queryClient.setQueryData(['admin', 'reviews', status], data);
          }
        });
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to delete review. Please try again.';

      toaster.create({
        title: 'Error deleting review',
        description: errorMessage,
        type: 'error',
      });

      console.error('[useDeleteReview] Error:', error);
    },
    onSuccess: () => {
      toaster.create({
        title: 'Review deleted successfully',
        description: 'The review has been permanently removed.',
        type: 'success',
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
    },
  });

  return {
    deleteReview: mutation.mutate,
    deleteReviewAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
}

